// Chat resume on care (BUG-011, Waseem 2026-10-26).
//
// A patient who loses their tab mid-consultation gets a resume link by email and SMS. The cloud function that mints
// it (yourdoctors-cloudfunctions functionsV2/chatResume/mint.js) sends chats started on care (startedOn: 'care' on
// the openconversations record) to care's own /resume?t=<token> instead of the function's page, which looks like
// the old registration chat. public/index.html takes the token out of the address before any tracker loads; this
// module redeems it at the chatResume function (GET ?redeem=1&t=, answered with CORS for care's hosts), signs the
// patient in with the custom token it returns, and lets the normal start-up send them to their chat.
//
// A link that is closed, unknown or fails goes to the function's own page, which already explains each case
// ("Your consultation has ended", "This link is not valid", try again).
//
// Needs REACT_APP_FIREBASE_CLOUD_FUNCTION_URL (the us-central1 functions host of the build's project).

const TOKEN_KEY = 'ydo_resume_token'

/** The chatResume function's address for this build's Firebase project. */
export const chatResumeUrl = () =>
  `${process.env.REACT_APP_FIREBASE_CLOUD_FUNCTION_URL}/chatResume`

/**
 * Fields added to a new openconversations record so the resume link brings the patient back to care.
 *
 * @param {string} variant the app's flow ('b' is care).
 * @returns {object} { startedOn: 'care' } for care, otherwise nothing (register's chats keep the function page).
 */
export const chatOriginFields = (variant) =>
  variant === 'b' ? { startedOn: 'care' } : {}

/** How long to wait for the redeem and the sign-in before handing over to the function's page (the handoff fetch in
    routes.js uses the same 10 s for the same reason: the patient only sees the splash meanwhile). */
export const REDEEM_TIMEOUT_MS = 10000

/**
 * The resume token index.html set aside for this page load, or null. index.html keeps it in sessionStorage, or behind
 * a one-shot getter when storage is blocked, never in a page global. Also reads ?t= on /resume directly, for a page
 * whose index.html predates the script.
 *
 * @returns {string|null}
 */
export function takeResumeToken() {
  let token = null
  try {
    token = window.sessionStorage.getItem(TOKEN_KEY)
  } catch (e) {
    token = null
  }
  if (!token && typeof window.__ydoTakeResumeToken === 'function') {
    token = window.__ydoTakeResumeToken()
    delete window.__ydoTakeResumeToken
  }
  if (!token && window.location.pathname.replace(/\/+$/, '') === '/resume') {
    token = new URLSearchParams(window.location.search).get('t')
    window.history.replaceState(window.history.state, '', '/')
  }
  return token ? String(token).trim() || null : null
}

/** Forgets the stored token, once it has been used or sent on to the function's page. */
export function forgetResumeToken() {
  try {
    window.sessionStorage.removeItem(TOKEN_KEY)
  } catch (e) {
    // storage blocked: nothing was stored
  }
}

/**
 * Redeems a resume token and signs the patient in.
 *
 * @param {string} token the resume token from the link.
 * @param {object} deps
 * @param {function} deps.signIn (customToken) => Promise, e.g. auth.signInWithCustomToken.
 * @param {function} [deps.fetchFn] fetch, replaceable in tests.
 * @param {function} [deps.goTo] (url) => void, leaves the page; defaults to window.location.replace.
 * @param {number} [deps.timeoutMs] how long to wait for the redeem and the sign-in together (REDEEM_TIMEOUT_MS).
 *   On a failure the page goes to the function's page with from=care-<reason> (closed, unknown, gone, error,
 *   redeem-failed, signin-failed), which the function's request logs record.
 * @returns {Promise<'signed-in'|'sent-to-function'>} 'signed-in' when the patient is signed in and the app should
 *   start as usual; 'sent-to-function' when the page is leaving for the function's own page (closed, unknown or
 *   failed link), so the app should not start.
 */
export async function resumeChat(token, { signIn, fetchFn = fetch, goTo, timeoutMs = REDEEM_TIMEOUT_MS } = {}) {
  const leave = goTo || ((url) => window.location.replace(url))
  // from=care lets the function's logs count patients care could not sign in (they would see BUG-011 again).
  const functionPage = (why) =>
    `${chatResumeUrl()}?t=${encodeURIComponent(token)}&from=${encodeURIComponent(`care-${why}`)}`
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null
  // One deadline covers the redeem and the sign-in, so a stalled connection never leaves the patient on the splash.
  let timedOut = false
  let timer = null
  const deadline = new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      timedOut = true
      if (ctrl) ctrl.abort()
      reject(new Error('chat resume timed out'))
    }, timeoutMs)
  })
  deadline.catch(() => {})
  let step = 'redeem'
  try {
    const res = await Promise.race([
      fetchFn(
        `${chatResumeUrl()}?redeem=1&t=${encodeURIComponent(token)}`,
        ctrl ? { signal: ctrl.signal } : undefined,
      ),
      deadline,
    ])
    const result = await Promise.race([res.json(), deadline])
    if (!result || !result.ok || !result.customToken) {
      clearTimeout(timer)
      forgetResumeToken()
      leave(functionPage((result && result.reason) || 'error'))
      return 'sent-to-function'
    }
    step = 'signin'
    await Promise.race([signIn(result.customToken), deadline])
    clearTimeout(timer)
    forgetResumeToken()
    return 'signed-in'
  } catch (e) {
    // Network, timeout or sign-in failure: the function's page retries and explains.
    clearTimeout(timer)
    if (timedOut && step === 'signin') console.error('chat resume: sign-in timed out')
    console.error(`chat resume: ${step} failed`, e)
    forgetResumeToken()
    leave(functionPage(step === 'signin' ? 'signin-failed' : 'redeem-failed'))
    return 'sent-to-function'
  }
}
